import { z } from 'zod';
import type { GenderSchema } from '../const/genderSchema';

export const vkPhotoSizeSchema = z.object({
    url: z.string().url(),
    width: z.number().optional(),
    height: z.number().optional(),
});

export type VkPhotoSizeSchema = z.infer<typeof vkPhotoSizeSchema>;

const vkPhotoSchema = z.object({
    orig_photo: z.object({ url: z.string().url() }).optional(),
    sizes: z.array(vkPhotoSizeSchema).default([]),
});

export type VkPhotoSchema = z.infer<typeof vkPhotoSchema>;

export const vkCategorySchema: z.ZodType<{
    id?: number;
    name: string;
    parent?: unknown;
}> = z.lazy(() =>
    z
        .object({
            id: z.number().optional(),
            name: z.string(),
            parent: vkCategorySchema.optional(),
        })
        .passthrough(),
);
export type VkCategory = z.infer<typeof vkCategorySchema>;

export const vkProductSchema = z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().optional(),
    category: vkCategorySchema.optional(),
    photos: z.array(vkPhotoSchema).default([]),
});

export const vkPayloadSchema = z.object({
    products: z.array(vkProductSchema),
});

const deriveGenderFromCategory = (cat?: VkCategory): GenderSchema => {
    if (!cat) return 'Unisex';

    let hasMale = false;
    let hasFemale = false;

    let cur: VkCategory | undefined = cat;
    while (cur) {
        const n = cur.name.toLowerCase();
        if (n.includes('мужчинам')) hasMale = true;
        if (n.includes('женщинам')) hasFemale = true;
        cur = cur.parent as VkCategory | undefined;
    }

    if (hasFemale && !hasMale) return 'Female';
    if (hasMale && !hasFemale) return 'Male';
    return 'Unisex';
};

export const vkProductWithGenderSchema = vkProductSchema.transform(p => ({
    ...p,
    gender: deriveGenderFromCategory(p.category),
}));
export type VkProductWithGender = z.infer<typeof vkProductWithGenderSchema>;

export const vkPayloadWithGenderSchema = z.object({
    products: z.array(vkProductWithGenderSchema),
});
export type VkPayloadWithGenderSchema = z.infer<typeof vkPayloadWithGenderSchema>;
